// Passing Difficulty Bands widget (Players canvas, `player-passing-difficulty`)
// — old Catalyst's "Difficulty Bands" detail tile: PSAX (pass success above
// expectation) by difficulty band as a diverging bar chart around zero, plus an
// exact-numbers table. Answers "how well does he execute the HARD passes he
// tries?". Positional basis; totals (no per-90). Hand-drawn SVG (no soccer-vis).

import type { WidgetProps } from "./registry.js";
import { theme } from "../lib/theme.js";
import { buildBands, type DifficultyBand } from "./passing.js";
import { usePassingComparison } from "./usePassingComparison.js";
import PassingScope from "./PassingScope.js";

// Band colours match the Style widget's attempt mix (hard→easy).
const COLOR: Record<DifficultyBand["id"], string> = {
  hard: "#a78bfa",
  medium: "#60a5fa",
  easy: "#34d399",
};
const BAND_KEYS = ["hard", "medium", "easy"].flatMap((b) => [
  `passes_${b}`,
  `passes_${b}_successful`,
  `psax_${b}`,
  `${b}_pass_success_perc`,
]);

// Diverging chart geometry.
const VIEW_W = 320;
const ROW_H = 30;
const AXIS_H = 14;
const LABEL_W = 56; // left gutter for the band name
const PLOT_L = LABEL_W;
const PLOT_R = VIEW_W - 44; // right gutter for the value label
const MID = (PLOT_L + PLOT_R) / 2;

export default function PassingDifficultyWidget({ scope }: WidgetProps) {
  const { playerId, competitionId, seasonId } = scope;
  const cmp = usePassingComparison(playerId, competitionId, seasonId, BAND_KEYS);

  if (playerId === null) return <Msg>Search for a player above.</Msg>;
  if (competitionId === null || seasonId === null)
    return <Msg>Pick a competition and season above.</Msg>;
  if (cmp.isPending) return <Msg>Loading difficulty bands…</Msg>;
  if (cmp.isError) return <Msg danger>Couldn't load difficulty bands.</Msg>;
  if (!cmp.playerRow) return <Msg>No passing data for this player in this season.</Msg>;

  const bands = buildBands(cmp.playerRow);
  const hasData = bands.some((b) => (b.attempted ?? 0) > 0);
  if (!hasData) return <Msg>No pass-difficulty data for this player in this season.</Msg>;

  const absMax = Math.max(0.5, ...bands.map((b) => Math.abs(b.psax ?? 0)));
  const halfW = MID - PLOT_L;
  const x = (psax: number) => MID + (psax / absMax) * halfW;
  const svgH = bands.length * ROW_H + AXIS_H;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 8, overflowY: "auto" }}>
      <PassingScope cmp={cmp} />

      <div style={{ color: theme.inkFaint, fontSize: 11 }}>Execution vs model (PSAX) by band</div>
      <svg viewBox={`0 0 ${VIEW_W} ${svgH}`} style={{ width: "100%", height: "auto", display: "block" }} role="img" aria-label="PSAX by difficulty band">
        {/* Zero guide line */}
        <line x1={MID} y1={0} x2={MID} y2={bands.length * ROW_H} stroke="rgba(148,163,184,0.35)" strokeWidth={1} />
        {bands.map((b, i) => {
          const cy = i * ROW_H + ROW_H / 2;
          const psax = b.psax ?? 0;
          const bx = x(psax);
          return (
            <g key={b.id}>
              <text x={PLOT_L - 6} y={cy + 3} textAnchor="end" fontSize={9} fill={theme.inkMuted}>{b.label}</text>
              <rect
                x={Math.min(MID, bx)}
                y={cy - 6}
                width={Math.max(1, Math.abs(bx - MID))}
                height={12}
                fill={COLOR[b.id]}
                rx={1.5}
              >
                <title>{`${b.label}: ${fmtSigned(b.psax)} PSAX (completed ${fmtInt(b.completed)} of ${fmtInt(b.attempted)}, expected ${fmt1(b.expected)})`}</title>
              </rect>
              <text x={psax >= 0 ? bx + 4 : bx - 4} y={cy + 3} textAnchor={psax >= 0 ? "start" : "end"} fontSize={9} fill={theme.ink} style={{ fontVariantNumeric: "tabular-nums" }}>
                {fmtSigned(b.psax)}
              </text>
            </g>
          );
        })}
        {/* Axis labels: negative left, positive right */}
        <text x={PLOT_L} y={svgH - 3} fontSize={8} fill={theme.inkFaint}>← below model</text>
        <text x={PLOT_R} y={svgH - 3} textAnchor="end" fontSize={8} fill={theme.inkFaint}>beats model →</text>
      </svg>

      {/* Exact numbers */}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, fontVariantNumeric: "tabular-nums" }}>
        <thead>
          <tr style={{ color: theme.inkFaint }}>
            <Th align="left">Band</Th><Th>Att</Th><Th>Comp</Th><Th>Exp</Th><Th>Succ%</Th><Th>xP%</Th><Th>PSAX</Th>
          </tr>
        </thead>
        <tbody>
          {bands.map((b) => (
            <tr key={b.id} style={{ borderTop: `1px solid ${theme.border}` }}>
              <Td align="left">
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 2, background: COLOR[b.id], marginRight: 6 }} />
                {b.label}
              </Td>
              <Td>{fmtInt(b.attempted)}</Td>
              <Td>{fmtInt(b.completed)}</Td>
              <Td>{fmt1(b.expected)}</Td>
              <Td>{fmtPct(b.successPerc)}</Td>
              <Td>{fmtPct(b.expectedPerc)}</Td>
              <Td>{fmtSigned(b.psax)}</Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, align = "right" }: { children: React.ReactNode; align?: "left" | "right" }) {
  return <th style={{ textAlign: align, fontWeight: 500, padding: "3px 4px" }}>{children}</th>;
}
function Td({ children, align = "right" }: { children: React.ReactNode; align?: "left" | "right" }) {
  return <td style={{ textAlign: align, color: theme.ink, padding: "3px 4px" }}>{children}</td>;
}

function fmtInt(v: number | null): string {
  return v === null ? "—" : v.toFixed(0);
}
function fmt1(v: number | null): string {
  return v === null ? "—" : v.toFixed(1);
}
function fmtPct(v: number | null): string {
  return v === null ? "—" : `${(v * 100).toFixed(1)}%`;
}
function fmtSigned(v: number | null): string {
  if (v === null) return "—";
  const s = v.toFixed(1);
  return v > 0 ? `+${s}` : s.replace("-", "−");
}

function Msg({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div style={{ color: danger ? theme.danger : theme.inkMuted, fontSize: 13 }}>{children}</div>;
}
